import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

import Input from '../../components/ui/Input';

const MarketingWebsiteLandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    department: '',
    startDate: '',
    endDate: '',
    employmentType: ''
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Green Header Navigation */}
      <header className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo Section */}
            <div className="flex items-center gap-3">
              <div className="text-sm text-emerald-100 italic">{t('marketing.companyLogoPlaceholder')}</div>
              <div className="text-xl font-bold">{t('marketing.companyName')}</div>
            </div>

            {/* Navigation Menu */}
            <nav className="hidden lg:flex items-center gap-1">
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium transition-colors">
                <Icon name="Sparkles" size={18} />
                {t('marketing.demo')}
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors">
                <Icon name="BarChart3" size={18} />
                {t('marketing.dashboard')}
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors">
                <Icon name="Bell" size={18} />
                {t('marketing.notifications')}
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors">
                <Icon name="Users" size={18} />
                {t('marketing.workforce')}
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors">
                <Icon name="DollarSign" size={18} />
                {t('marketing.payroll')}
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors">
                <Icon name="UserCheck" size={18} />
                {t('marketing.selfService')}
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors">
                <Icon name="MoreHorizontal" size={18} />
                {t('marketing.more')}
              </button>
            </nav>

            {/* Right Side Icons */}
            <div className="flex items-center gap-3">
              <button className="p-2 rounded-lg hover:bg-emerald-600 transition-colors">
                <Icon name="Bell" size={20} />
              </button>
              <button className="p-2 rounded-lg hover:bg-emerald-600 transition-colors bg-purple-600">
                <Icon name="Moon" size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Dark Filters Section */}
      <section className="bg-slate-800 text-white py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-6">
            <Icon name="Filter" size={20} className="text-blue-400" />
            <h2 className="text-xl font-semibold">{t('marketing.filters')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Department Filter */}
            <div>
              <label className="block text-sm font-medium mb-2">{t('marketing.department')}</label>
              <Input
                type="text"
                placeholder={t('marketing.selectDepartment')}
                value={filters?.department}
                onChange={(e) => handleFilterChange('department', e?.target?.value)}
                className="w-full bg-white dark:bg-slate-700 border-slate-600 text-gray-900 dark:text-white"
              />
            </div>

            {/* Start Date Filter */}
            <div>
              <label className="block text-sm font-medium mb-2">{t('marketing.startDate')}</label>
              <Input
                type="date"
                placeholder="dd-----yyyy"
                value={filters?.startDate}
                onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
                className="w-full bg-slate-700 border-slate-600 text-white"
              />
            </div>

            {/* End Date Filter */}
            <div>
              <label className="block text-sm font-medium mb-2">{t('marketing.endDate')}</label>
              <Input
                type="date"
                placeholder="dd-----yyyy"
                value={filters?.endDate}
                onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
                className="w-full bg-slate-700 border-slate-600 text-white"
              />
            </div>

            {/* Employment Type Filter */}
            <div>
              <label className="block text-sm font-medium mb-2">{t('marketing.employmentType')}</label>
              <Input
                type="text"
                placeholder={t('marketing.selectType')}
                value={filters?.employmentType}
                onChange={(e) => handleFilterChange('employmentType', e?.target?.value)}
                className="w-full bg-white dark:bg-slate-700 border-slate-600 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>
      </section>
      {/* Welcome Section with Green Background */}
      <section className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800 text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Platform Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 bg-emerald-500/30 backdrop-blur-sm border border-emerald-400/50 rounded-full px-6 py-2">
              <Icon name="Sparkles" size={18} className="text-emerald-200" />
              <span className="text-sm font-medium">{t('marketing.platformBadge')}</span>
            </div>
          </div>

          {/* Main Heading */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {t('marketing.welcomeHeading')}
            </h1>
            <p className="text-xl text-emerald-50 leading-relaxed">
              {t('marketing.welcomeSubheading')}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            {user ? (
              <Link
                to="/main-dashboard-hub"
                className="group relative px-8 py-4 bg-white text-emerald-600 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transition-all hover:scale-105"
              >
                <span className="flex items-center gap-2">
                  <Icon name="LayoutGrid" size={20} />
                  {t('marketing.goToDashboard')}
                </span>
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="group relative px-8 py-4 bg-white text-emerald-600 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                >
                  <span className="flex items-center gap-2">
                    <Icon name="LogIn" size={20} />
                    {t('marketing.signIn')}
                  </span>
                </Link>
                <Link
                  to="/signup"
                  className="group relative px-8 py-4 bg-emerald-700 text-white rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transition-all hover:scale-105 border-2 border-white"
                >
                  <span className="flex items-center gap-2">
                    <Icon name="UserPlus" size={20} />
                    {t('marketing.getStartedFree')}
                  </span>
                </Link>
              </>
            )}
          </div>

          {/* Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Dashboards Card */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Icon name="CheckCircle2" size={28} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">{t('marketing.dashboardsCount')}</div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('marketing.dashboardsTitle')}</h3>
              <p className="text-gray-600 dark:text-gray-400">{t('marketing.dashboardsDescription')}</p>
            </div>

            {/* Compliance Card */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-2 border-amber-400">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Icon name="TrendingUp" size={28} className="text-amber-600 dark:text-amber-400" />
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-amber-600 dark:text-amber-400">{t('marketing.compliancePercent')}</div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('marketing.complianceTitle')}</h3>
              <p className="text-gray-600 dark:text-gray-400">{t('marketing.complianceDescription')}</p>
            </div>

            {/* Real-time Card */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Icon name="Clock" size={28} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">{t('marketing.realtimeAvailability')}</div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('marketing.realtimeTitle')}</h3>
              <p className="text-gray-600 dark:text-gray-400">{t('marketing.realtimeDescription')}</p>
            </div>
          </div>
        </div>
      </section>
      {/* Footer Section */}
      <footer className="bg-slate-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm">
              {t('marketing.footerCopyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingWebsiteLandingPage;